// DEPENDENCIES
import { useState } from "react";

// TYPES
import { Step } from "../../types";

// COMPONENTS
import { Alert } from "@allaria-tech/matter/Alert";
import { ButtonGroup } from "@allaria-tech/matter/ButtonGroup";
import { Icon } from "@allaria-tech/matter/Icon";
import {
  TextField,
  getCountryPrefixOptions,
} from "@allaria-tech/matter/TextField";
import { ToastMessage } from "@allaria-tech/matter/ToastMessage";
import { Typography } from "@allaria-tech/matter/Typography";
import DotStep from "../DotStep/DotStep";

// UTILS
import { postToParent } from "../../utils/crypto";
import useForm from "../../hooks/useForm";
import useMobileLayout from "../../hooks/useMobileLayout";
import { phone as phoneSchema, internationalPhone } from "../../validations";
import {
  fieldRequiredMessage,
  internationalPhoneNumberMessage,
  phoneNumberRequiredMessage,
} from "../../i18n/validationMessages";

// STYLES
import styles from "./CryptoPhoneStep.module.scss";

const PHONE_PREFIX_OPTIONS = getCountryPrefixOptions({
  preferred: ["AR", "ES", "UY"],
});

// `phone` has no upper bound for foreign numbers; `internationalPhone` caps
// them at 15 digits.
const phoneValidation = phoneSchema.concat(internationalPhone);

type Props = {
  dispatch: Parameters<NonNullable<Step["component"]>>[0];
  state: Parameters<NonNullable<Step["component"]>>[1];
  formik: Parameters<NonNullable<Step["component"]>>[2];
};

// Only collects the phone: it is sent with the rest of the account data when the
// last step completes the onboarding.
const CryptoPhoneStep = ({ formik }: Props) => {
  const { handleContinue } = useForm(formik);

  const { isMobile } = useMobileLayout();

  const [phoneTouched, setPhoneTouched] = useState(false);

  // In Formik next to the phone, not in local state: coming back from the last
  // step remounts this, and the number would show under the default country.
  const phonePrefix: string = formik?.values?.phonePrefix ?? "AR";
  const setPhonePrefix = (prefix: string) =>
    formik?.setFieldValue("phonePrefix", prefix);

  const phone = formik?.values?.phone ?? "";
  const phoneDialCode = PHONE_PREFIX_OPTIONS.find(
    (option) => option.value === phonePrefix,
  )?.dialCode;
  const isPhoneValid = phoneValidation.isValidSync(phone);
  const showPhoneError = phoneTouched && !isPhoneValid;
  const phoneError = !phone
    ? fieldRequiredMessage.es
    : phonePrefix === "AR"
      ? phoneNumberRequiredMessage.es
      : internationalPhoneNumberMessage.es;

  // Right after the identity is validated there is nothing to go back to: the
  // way out closes the onboarding.
  const handleClose = () => postToParent("DIGITAL_ONBOARDING_CLOSE");

  return (
    <section className={`${styles.container} ${isMobile ? styles.phone : ""}`}>
      <ToastMessage messageId="formAlert" position="TOP" />
      {/* It closes the identity screens: their header, as FormContainer draws it. */}
      <div className={styles.titleRow}>
        <Typography
          type="HEADING"
          size={isMobile ? "M" : "L"}
          weight="BASE"
          color={isMobile ? "var(--neutrals-600)" : "var(--primary-300)"}
        >
          Identidad
        </Typography>
        <DotStep current={5} total={5} />
      </div>

      {isMobile && (
        <div className={styles.intro}>
          <Icon name="telephone" size={80} color="var(--icon-base, #3f4f69)" />
          <div className={styles.introText}>
            <Typography
              type="HEADING"
              size="M"
              color="var(--typography-heading-color-base)"
            >
              Vamos a validar tu número de teléfono
            </Typography>
            <Typography
              type="BODY"
              size="S"
              color="var(--typography-body-color-base)"
            >
              Necesitamos que nos confirmes tu número celular para habilitar las
              distintas transacciones de activos digitales.
            </Typography>
          </div>
        </div>
      )}

      <div className={styles.fields}>
        <TextField
          name="phone"
          variant="phone"
          label="Teléfono"
          inputMode="tel"
          prefixOptions={PHONE_PREFIX_OPTIONS}
          selectedPrefix={phonePrefix}
          onPrefixChange={setPhonePrefix}
          // The app's modal sizes the frame to its content: past it the list
          // would be cut.
          fitPrefixMenuToAvailableHeight
          value={phone}
          // Matter prepends the dial code even when the field is cleared;
          // keep it empty so it reads as missing, not as a short number.
          onChange={(value: string) =>
            formik?.setFieldValue("phone", value === phoneDialCode ? "" : value)
          }
          onBlur={() => setPhoneTouched(true)}
          isInvalid={showPhoneError}
          isValid={phoneTouched && isPhoneValid}
          touched={phoneTouched}
          errorMessage={phoneError}
          // Matter keeps the subtext under the error; the design swaps them.
          subtext={
            isMobile && !showPhoneError
              ? {
                  text: "Se utilizará para validar la cuenta con la verificación en dos pasos (2FA)",
                  type: "info",
                }
              : undefined
          }
        />
      </div>

      {!isMobile && (
        <Alert variant="INFO" className={styles.alert}>
          Se utilizará para validar la cuenta y distintos tipos de transacciones
          a través de la verificación en dos pasos (2FA).
        </Alert>
      )}

      <ButtonGroup
        container={isMobile}
        containerClassName={styles.buttons}
        align="center"
        disableMobileLayout={!isMobile}
        secondary={{ text: "Cerrar", type: "button", onClick: handleClose }}
        primary={{
          text: "Continuar",
          type: "button",
          isDisabled: !isPhoneValid,
          onClick: handleContinue,
        }}
      />
    </section>
  );
};

export default CryptoPhoneStep;
